import { useLayoutEffect, useRef } from "react";

// Freezes everything above a grid (tiles, toolbar, tabs) under the 64px page header.
// Publishes its height as --sticky-a / --sticky-b so a `.thead-sticky` grid header
// can pin itself directly underneath. `slot="b"` stacks below a slot-"a" block.
// Also marks each sibling `.data-table-container` with `.fits` when its table needs
// no horizontal scroll — only then does the grid header freeze (clip would hide columns).
// The vars live on the enclosing page container (not <html>) so each split-screen pane keeps its own offsets.
export default function StickyTop({ children, slot = "a", className = "", testId }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const scope = el.closest(".top-layout-content") || document.documentElement;
    const name = `--sticky-${slot}`;
    const page = el.parentElement;
    const markFits = () => {
      page?.querySelectorAll(".data-table-container").forEach((c) => {
        const table = c.querySelector("table");
        const wide = !!table && table.offsetWidth > c.clientWidth + 1;
        // Pin only while rows still run below the pinned header; past that a sticky thead would slide to the table's bottom.
        const pin = 64 + (parseFloat(scope.style.getPropertyValue("--sticky-a")) || 0) + (parseFloat(scope.style.getPropertyValue("--sticky-b")) || 0);
        const headH = table?.querySelector("thead")?.offsetHeight || 0;
        if (headH) scope.style.setProperty("--thead-h", `${headH}px`);
        const tall = !!table && table.getBoundingClientRect().bottom > pin + headH + 8;
        c.classList.toggle("fits", !table || (!wide && tall));
      });
    };
    const set = () => { scope.style.setProperty(name, `${el.offsetHeight}px`); markFits(); };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    if (page) ro.observe(page);
    const mo = page ? new MutationObserver(markFits) : null;
    mo?.observe(page, { childList: true, subtree: true });
    // Capture phase: fires for window scroll AND for a split pane scrolling on its own.
    document.addEventListener("scroll", markFits, { capture: true, passive: true });
    return () => { ro.disconnect(); mo?.disconnect(); document.removeEventListener("scroll", markFits, { capture: true }); scope.style.setProperty(name, "0px"); };
  }, [slot]);
  return (
    <div ref={ref} className={`sticky-top sticky-top-${slot} ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}
